import React from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import TableCellBase, { tableCellClasses } from "@mui/material/TableCell";
import TableRowBase from "@mui/material/TableRow";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import BlockIcon from "@mui/icons-material/Block";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import { ContextSession } from "../../services/ContextSession";
import { useNavigate } from "react-router-dom";

const API_FALLBACK = "https://bei360.injupemp.gob.hn:8444/";
//const API_FALLBACK = "http://127.0.0.1:8000/";
const PLATFORM_OPTIONS = [
  { value: "DOMINIO", label: "Dominio" },
  { value: "AS400", label: "AS400" },
  { value: "OFFICE", label: "Office" },
];

const StyledTableCell = styled(TableCellBase)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#19438C",
    color: "white",
    fontSize: 15,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRowBase)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

function formatDate(value) {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString("es-HN");
}

function buildRequestError(response, data, fallbackMessage) {
  const error = new Error(
    data?.error || data?.message || fallbackMessage || "No se pudo completar la solicitud.",
  );
  error.status = response.status;
  error.code = data?.code;
  error.payload = data;
  return error;
}

async function requestJson(url, options = {}) {
  const response = await fetch(url, options);
  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw buildRequestError(response, data, "No se pudo completar la solicitud.");
  }

  return data;
}

export default function BlacklistPage() {
  const sessi = React.useContext(ContextSession);
  const navigate = useNavigate();
  const apiBaseUrl = API_FALLBACK;
  const accessToken = sessi?.getToken ? sessi.getToken() : "";

  const [searchText, setSearchText] = React.useState("");
  const [employeeOptions, setEmployeeOptions] = React.useState([]);
  const [employeeLoading, setEmployeeLoading] = React.useState(false);
  const [selectedEmployee, setSelectedEmployee] = React.useState(null);
  const [platform, setPlatform] = React.useState("");
  const [as400User, setAs400User] = React.useState("");
  const [rows, setRows] = React.useState([]);
  const [rowsLoading, setRowsLoading] = React.useState(false);
  const [submitLoading, setSubmitLoading] = React.useState(false);
  const [deleteLoading, setDeleteLoading] = React.useState(false);
  const [deleteTarget, setDeleteTarget] = React.useState(null);
  const [errorMsg, setErrorMsg] = React.useState("");
  const [successMsg, setSuccessMsg] = React.useState("");

  const canCreate =
    !!selectedEmployee &&
    !!platform &&
    (platform !== "AS400" || as400User.trim().length >= 2);

  const fetchBlacklist = React.useCallback(async () => {
    if (!selectedEmployee?.UserID || !platform) {
      setRows([]);
      return;
    }

    setRowsLoading(true);
    setErrorMsg("");

    try {
      const params = new URLSearchParams({
        user_id: String(selectedEmployee.UserID),
        plataforma: platform,
      });

      const data = await requestJson(
        `${apiBaseUrl}api/blacklist/?${params.toString()}`,
        {
          method: "GET",
          headers: {
            Accept: "application/json",
            ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
          },
        },
      );

      setRows(Array.isArray(data) ? data : []);
    } catch (error) {
      if (error?.status === 401 && error?.code === "token_not_valid") {
        sessi.clearSession();
        navigate("/");
        return;
      }
      setRows([]);
      setErrorMsg(error.message || "No se pudo cargar la blacklist.");
    } finally {
      setRowsLoading(false);
    }
  }, [accessToken, apiBaseUrl, navigate, platform, selectedEmployee, sessi]);

  React.useEffect(() => {
    if (searchText.trim().length < 2) {
      setEmployeeOptions([]);
      setEmployeeLoading(false);
      return undefined;
    }

    let active = true;
    setEmployeeLoading(true);

    const timeoutId = window.setTimeout(async () => {
      try {
        const params = new URLSearchParams({ search: searchText.trim() });
        const data = await requestJson(
          `${apiBaseUrl}api/blacklist/users-siac/?${params.toString()}`,
          {
            method: "GET",
            headers: {
              Accept: "application/json",
              ...(accessToken
                ? { Authorization: `Bearer ${accessToken}` }
                : {}),
            },
          },
        );

        if (active) {
          setEmployeeOptions(Array.isArray(data) ? data : []);
        }
      } catch (error) {
        if (error?.status === 401 && error?.code === "token_not_valid") {
          sessi.clearSession();
          navigate("/");
          return;
        }
        if (active) {
          setEmployeeOptions([]);
          setErrorMsg(error.message || "No se pudo buscar el empleado.");
        }
      } finally {
        if (active) {
          setEmployeeLoading(false);
        }
      }
    }, 350);

    return () => {
      active = false;
      window.clearTimeout(timeoutId);
    };
  }, [accessToken, apiBaseUrl, navigate, searchText, sessi]);

  React.useEffect(() => {
    fetchBlacklist();
  }, [fetchBlacklist]);

  const handleEmployeeChange = (event, newValue) => {
    setSelectedEmployee(newValue);
    setRows([]);
    setErrorMsg("");
    setSuccessMsg("");
  };

  const handlePlatformChange = (event) => {
    const newPlatform = event.target.value;
    setPlatform(newPlatform);
    if (newPlatform !== "AS400") {
      setAs400User("");
    }
    setRows([]);
    setErrorMsg("");
    setSuccessMsg("");
  };

  const handleCreate = async () => {
    if (!canCreate || submitLoading) return;

    setSubmitLoading(true);
    setErrorMsg("");
    setSuccessMsg("");

    try {
      const payload = {
        user_id: selectedEmployee.UserID,
        plataforma: platform,
        usuario_plataforma:
          platform === "AS400" ? as400User.trim().toUpperCase() : "",
      };

      const data = await requestJson(`${apiBaseUrl}api/blacklist/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        },
        body: JSON.stringify(payload),
      });

      setSuccessMsg(data?.message || "Registro agregado correctamente.");
      await fetchBlacklist();
      if (platform === "AS400") {
        setAs400User("");
      }
    } catch (error) {
      if (error?.status === 401 && error?.code === "token_not_valid") {
        sessi.clearSession();
        navigate("/");
        return;
      }
      setErrorMsg(error.message || "No se pudo agregar a la blacklist.");
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget?.Id || deleteLoading) return;

    setDeleteLoading(true);
    setErrorMsg("");
    setSuccessMsg("");

    try {
      const data = await requestJson(
        `${apiBaseUrl}api/blacklist/${deleteTarget.Id}/`,
        {
          method: "DELETE",
          headers: {
            Accept: "application/json",
            ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
          },
        },
      );

      setSuccessMsg(data?.message || "Registro eliminado correctamente.");
      setDeleteTarget(null);
      await fetchBlacklist();
    } catch (error) {
      if (error?.status === 401 && error?.code === "token_not_valid") {
        sessi.clearSession();
        navigate("/");
        return;
      }
      setErrorMsg(error.message || "No se pudo eliminar el registro.");
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <Box sx={{ px: { xs: 2, md: 4 }, py: { xs: 3, md: 5 } }}>
      <Stack spacing={3}>
        <Paper elevation={3} sx={{ p: { xs: 2, md: 3.5 }, borderRadius: 4 }}>
          <Stack spacing={3}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              justifyContent="space-between"
              spacing={1.5}
            >
              <Box>
                <Typography variant="h4" fontWeight={900}>
                  BlackList
                </Typography>
                <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                  Registra bajas por empleado y plataforma en el modulo UTI.
                </Typography>
              </Box>

              <Chip
                icon={<BlockIcon />}
                label="Bajas UTI"
                color="error"
                variant="outlined"
              />
            </Stack>

            {errorMsg ? <Alert severity="error">{errorMsg}</Alert> : null}
            {successMsg ? <Alert severity="success">{successMsg}</Alert> : null}

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1.4fr 0.8fr" },
                gap: 2,
              }}
            >
              <Autocomplete
                options={employeeOptions}
                value={selectedEmployee}
                loading={employeeLoading}
                onChange={handleEmployeeChange}
                onInputChange={(event, newInputValue) =>
                  setSearchText(newInputValue)
                }
                getOptionLabel={(option) =>
                  `${option?.NombreCompleto || ""} - ${option?.Usuario || ""}`.trim()
                }
                isOptionEqualToValue={(option, value) =>
                  option.UserID === value.UserID
                }
                noOptionsText={
                  searchText.trim().length < 2
                    ? "Escribe al menos 2 caracteres"
                    : "Sin resultados"
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Empleado"
                    placeholder="Buscar por nombre o usuario"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <PersonSearchIcon
                            sx={{ mr: 1, color: "text.secondary" }}
                          />
                          {params.InputProps.startAdornment}
                        </>
                      ),
                      endAdornment: (
                        <>
                          {employeeLoading ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />

              <FormControl fullWidth>
                <InputLabel id="blacklist-platform-label">
                  Plataforma
                </InputLabel>
                <Select
                  labelId="blacklist-platform-label"
                  label="Plataforma"
                  value={platform}
                  onChange={handlePlatformChange}
                >
                  {PLATFORM_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {selectedEmployee ? (
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip label={`Usuario: ${selectedEmployee.Usuario || "N/A"}`} />
                <Chip
                  label={`Nombre: ${selectedEmployee.NombreCompleto || "N/A"}`}
                  variant="outlined"
                />
                <Chip
                  label={`DNI: ${selectedEmployee.DNI || "N/A"}`}
                  variant="outlined"
                />
              </Stack>
            ) : null}

            {platform === "AS400" ? (
              <TextField
                fullWidth
                label="Usuario AS400"
                placeholder="Ingresa el usuario AS400 a dar de baja"
                value={as400User}
                onChange={(event) =>
                  setAs400User(event.target.value.toUpperCase())
                }
                helperText="Para AS400 puedes registrar un usuario de plataforma especifico."
              />
            ) : null}

            <Stack
              direction={{ xs: "column", sm: "row" }}
              justifyContent="flex-end"
              spacing={1.5}
            >
              <Button
                variant="contained"
                color="error"
                disabled={!canCreate || submitLoading}
                onClick={handleCreate}
              >
                {submitLoading ? (
                  <CircularProgress size={22} sx={{ color: "white" }} />
                ) : (
                  "Agregar baja a la BlackList"
                )}
              </Button>
            </Stack>
          </Stack>
        </Paper>

        <Paper elevation={2} sx={{ p: { xs: 2, md: 3 }, borderRadius: 4 }}>
          <Stack spacing={2}>
            <Box>
              <Typography variant="h6" fontWeight={800}>
                Lista de BlackList
              </Typography>
              <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                Se muestran los registros del empleado seleccionado en la
                plataforma elegida.
              </Typography>
            </Box>

            {!selectedEmployee || !platform ? (
              <Alert severity="info">
                Selecciona un empleado y una plataforma para consultar la
                blacklist.
              </Alert>
            ) : null}

            {rowsLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress />
              </Box>
            ) : selectedEmployee && platform ? (
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <StyledTableCell>Empleado</StyledTableCell>
                      <StyledTableCell>Plataforma</StyledTableCell>
                      <StyledTableCell>Usuario Plataforma</StyledTableCell>
                      <StyledTableCell>Estado</StyledTableCell>
                      <StyledTableCell>Fecha Registro</StyledTableCell>
                      <StyledTableCell align="center">Acciones</StyledTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.length > 0 ? (
                      rows.map((row) => (
                        <StyledTableRow key={row.Id}>
                          <StyledTableCell>
                            {row.Nombre || row.Usuario}
                          </StyledTableCell>
                          <StyledTableCell>{row.Plataforma}</StyledTableCell>
                          <StyledTableCell>
                            {row.UsuarioPlataforma}
                          </StyledTableCell>
                          <StyledTableCell>{row.Estado}</StyledTableCell>
                          <StyledTableCell>
                            {formatDate(row.FechaRegistro)}
                          </StyledTableCell>
                          <StyledTableCell align="center">
                            <IconButton
                              color="error"
                              onClick={() => setDeleteTarget(row)}
                            >
                              <DeleteOutlineIcon />
                            </IconButton>
                          </StyledTableCell>
                        </StyledTableRow>
                      ))
                    ) : (
                      <StyledTableRow>
                        <StyledTableCell colSpan={6} align="center">
                          No hay registros en la blacklist para este empleado y
                          plataforma.
                        </StyledTableCell>
                      </StyledTableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : null}
          </Stack>
        </Paper>
      </Stack>

      <Dialog
        open={!!deleteTarget}
        onClose={() => (deleteLoading ? null : setDeleteTarget(null))}
      >
        <DialogTitle>Eliminar registro</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {deleteTarget
              ? `Se eliminara la baja de ${deleteTarget.UsuarioPlataforma} en ${deleteTarget.Plataforma}.`
              : ""}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteTarget(null)}
            disabled={deleteLoading}
          >
            Cancelar
          </Button>
          <Button color="error" onClick={handleDelete} disabled={deleteLoading}>
            {deleteLoading ? <CircularProgress size={20} /> : "Eliminar"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
